var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { showConfirmDialog, showCustomDialog } from '@juvare/common-web-ui/DialogHelper';
import { Alert, Box, Button, DialogActions, DialogContent, DialogTitle, LinearProgress, Typography } from '@mui/material';
import React from 'react';
import { parseCsvFile } from '../CsvFile';
import { calculateImportResult, ImportResult } from '../ImportResult';
import { SelectFileArea } from '../SelectFileArea';
export function showDataImportDialog(params) {
    showCustomDialog({
        maxWidth: 'sm',
        fullWidth: true,
        content: (close, ariaTitleId, ariaContentId) => (React.createElement(DataImportDialogComponent, Object.assign({}, params, { close: close, ariaTitleId: ariaTitleId, ariaContentId: ariaContentId }))),
    });
}
function DataImportDialogComponent(props) {
    const { close, init, validateRow, validateRows, importRow, importRows, onClose, onAfterImport, maxAllowedRowsCount } = props;
    const cancelled = React.useRef(false);
    const paused = React.useRef(false);
    const [errorMessage, setErrorMessage] = React.useState();
    const [file, setFile] = React.useState();
    const [csvFile, setCsvFile] = React.useState();
    const [progress, setProgress] = React.useState();
    const [context, setContext] = React.useState();
    const [importResult, setImportResult] = React.useState();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleError = React.useCallback((error) => {
        console.error(error);
        let message;
        if (error.message) {
            message = error.message;
        }
        else if (error.toString) {
            message = error.toString();
        }
        setErrorMessage(message || 'Unknown error');
    }, []);
    const processCsvFile = React.useCallback((csv) => {
        setCsvFile(csv);
        validateCsvFile(csv, cancelled, setProgress, init, validateRow, validateRows, maxAllowedRowsCount)
            .then((result) => {
            if (result) {
                setContext(result.context);
                setImportResult(calculateImportResult('validate', csv.header, result.rows));
            }
        })
            .catch(handleError);
    }, [handleError, init, validateRow, validateRows, maxAllowedRowsCount]);
    const processFile = React.useCallback((newFile) => {
        setFile(newFile);
        setProgress({ message: 'Reading CSV file', count: 0, total: 0, determinate: false });
        parseCsvFile(newFile).then(processCsvFile).catch(handleError);
    }, [handleError, processCsvFile]);
    const handleImport = React.useCallback(() => {
        if (csvFile && importResult && context) {
            importCsvFile(csvFile, importResult.rows, context, cancelled, paused, setProgress, importRow, importRows)
                .then((result) => {
                if (result) {
                    cancelled.current = false;
                    const calculatedResult = calculateImportResult('import', csvFile.header, result);
                    if (onAfterImport) {
                        onAfterImport(calculatedResult, context)
                            .then((message) => {
                            setImportResult(Object.assign(Object.assign({}, calculatedResult), { afterImportMessage: message }));
                        })
                            .catch(handleError);
                    }
                    else {
                        setImportResult(calculatedResult);
                    }
                }
            })
                .catch(handleError);
        }
    }, [handleError, csvFile, importResult, context, importRow, importRows, onAfterImport]);
    const handleCancel = React.useCallback(() => {
        if (importResult && importResult.type === 'validate' && progress && !errorMessage) {
            paused.current = true;
            showConfirmDialog({
                title: 'Cancel import?',
                onConfirm: () => {
                    cancelled.current = true;
                    paused.current = false;
                },
                onCancel: () => {
                    paused.current = false;
                },
            });
        }
        else {
            cancelled.current = true;
            if (onClose) {
                close(() => onClose(importResult, context));
            }
            else {
                close();
            }
        }
    }, [close, importResult, progress, onClose, errorMessage, context]);
    let dialogTitle = props.title;
    if (importResult && importResult.type === 'validate') {
        dialogTitle = 'Validation results';
    }
    else if (importResult && importResult.type === 'import') {
        dialogTitle = 'Upload results';
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(DialogTitle, { id: props.ariaTitleId }, dialogTitle),
        React.createElement(DialogContent, { id: props.ariaContentId },
            props.content,
            React.createElement(Box, { sx: { minHeight: 160 } },
                !errorMessage && progress && (React.createElement(Box, { sx: { paddingTop: '40px' } },
                    React.createElement(Typography, { p: 1, sx: {
                            textAlign: 'center',
                        } },
                        progress.message,
                        progress.determinate && progress.total !== 0
                            ? ' (' + (progress.count + 1) + ' of ' + progress.total + ')'
                            : ''),
                    progress.determinate && progress.total > 0 && (React.createElement(LinearProgress, { variant: 'determinate', value: ((progress.count + 1) / progress.total) * 100 })),
                    !progress.determinate && React.createElement(LinearProgress, { variant: 'indeterminate' }))),
                !errorMessage && !progress && !file && (React.createElement(SelectFileArea, { fileTypeLabel: 'CSV file', fileInputAccept: '.csv', file: file, onSelectFile: processFile })),
                !errorMessage && !progress && importResult && React.createElement(ImportResult, Object.assign({}, importResult)),
                errorMessage && (React.createElement(Alert, { severity: 'error' },
                    "Failed to process CSV file",
                    React.createElement("br", null),
                    errorMessage)))),
        React.createElement(DialogActions, null,
            (!importResult ||
                (importResult.type !== 'import' && (errorMessage || !progress || (progress && progress.determinate)))) && (React.createElement(Button, { color: 'primary', disabled: progress === null || progress === void 0 ? void 0 : progress.disableCancel, onClick: handleCancel }, errorMessage ? 'Ok' : 'Cancel')),
            importResult &&
                importResult.type === 'validate' &&
                !errorMessage &&
                (!!importResult.validRowCount || !!importResult.warningRowCount) && (React.createElement(Button, { color: 'primary', disabled: !!progress, onClick: handleImport, "data-test": 'import-button' }, !importResult.errorRowCount && !importResult.skippedRowCount ? 'Import' : 'Import Valid')),
            importResult && importResult.type === 'import' && (React.createElement(Button, { color: 'primary', onClick: handleCancel }, "Ok")))));
}
function validateCsvFile(csvFile, cancelled, setProgress, init, validateRow, validateRows, maxAllowedRowsCount) {
    return __awaiter(this, void 0, void 0, function* () {
        const message = 'Validating CSV file';
        setProgress({ message: message, count: 0, total: csvFile.rows.length, determinate: !!validateRow });
        const context = yield init(csvFile);
        let rows = csvFile.rows.map((row) => ({
            data: row,
            warnings: [],
            errors: [],
        }));
        if (cancelled.current) {
            return;
        }
        if (maxAllowedRowsCount && maxAllowedRowsCount > 0 && rows.length > maxAllowedRowsCount) {
            throw new Error(`Your file exceeds the size limit. Upload a CSV file that is ${maxAllowedRowsCount.toLocaleString()} rows or less.`);
        }
        if (validateRow) {
            const validatedRows = [];
            for (let i = 0; i < rows.length; i++) {
                if (cancelled.current) {
                    return;
                }
                const row = rows[i];
                setProgress({ message: message, count: i, total: csvFile.rows.length, determinate: true });
                validatedRows.push(yield validateRow(row, csvFile, context));
            }
            rows = validatedRows;
        }
        if (validateRows) {
            if (cancelled.current) {
                return;
            }
            rows = yield validateRows(rows, csvFile, context);
        }
        setProgress(undefined);
        return {
            context: context,
            rows: rows,
        };
    });
}
function importCsvFile(csvFile, rows, context, cancelled, paused, setProgress, importRow, importRows) {
    return __awaiter(this, void 0, void 0, function* () {
        const message = 'Importing CSV file';
        rows = rows.filter((row) => row.object != null && row.errors.length === 0);
        setProgress({ message: message, count: 0, total: rows.length, determinate: !!importRow });
        if (cancelled.current) {
            return;
        }
        if (importRow) {
            const importedRows = [];
            for (let i = 0; i < rows.length; i++) {
                if (cancelled.current) {
                    for (let j = i; j < rows.length; j++) {
                        importedRows.push(Object.assign(Object.assign({}, rows[j]), { errors: ['Import cancelled'] }));
                    }
                    setProgress(undefined);
                    return importedRows;
                }
                while (paused.current) {
                    yield sleep(1000);
                }
                const row = rows[i];
                setProgress({ message: message, count: i, total: rows.length, disableCancel: i + 1 === rows.length, determinate: true });
                try {
                    importedRows.push(yield importRow(row, csvFile, context));
                }
                catch (e) {
                    setProgress(undefined);
                    throw e;
                }
            }
            rows = importedRows;
        }
        if (importRows) {
            if (cancelled.current) {
                setProgress(undefined);
                return rows.map((row) => (Object.assign(Object.assign({}, row), { errors: ['Import cancelled'] })));
            }
            while (paused.current) {
                yield sleep(1000);
            }
            rows = yield importRows(rows, csvFile, context);
        }
        setProgress(undefined);
        return rows;
    });
}
function sleep(millis) {
    return new Promise((resolve) => window.setTimeout(resolve, millis));
}
